export const DEFAULT_AVATAR = "/img/user_avatar.jpg";

export const DEFAULT_FILTERS = {
  search: "",
  status: "",
  coach: "",
  members: "",
  sort: "nameAsc",
  perPage: 25,
};

export const STATUS_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "active", label: "Groupes actifs" },
  { value: "inactive", label: "Groupes inactifs" },
];

export const MEMBERS_OPTIONS = [
  { value: "", label: "Tous les groupes" },
  { value: "with", label: "Avec adhérents" },
  { value: "without", label: "Sans adhérent" },
];

export function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(Number(value || 0));
}

export function getGroupName(group) {
  return group?.nom || "Groupe sans nom";
}

export function getGroupUsers(group) {
  if (Array.isArray(group?.Users)) return group.Users;
  if (Array.isArray(group?.users)) return group.users;
  return [];
}

export function getGroupAdherents(group) {
  if (Array.isArray(group?.Adherents)) return group.Adherents;
  if (Array.isArray(group?.adherents)) return group.adherents;
  return [];
}

export function getGroupAdherentsCount(group) {
  const adherents = getGroupAdherents(group);

  return Number(group?.nombreAdherents ?? group?.adherents_count ?? adherents.length ?? 0);
}

export function getUserName(user) {
  return [user?.prenom, user?.nom].filter(Boolean).join(" ") || "Utilisateur";
}

export function getUserPhoto(user) {
  return user?.photo_url || user?.avatar_url || DEFAULT_AVATAR;
}

export function getAdherentName(adherent) {
  return [adherent?.prenom, adherent?.nom].filter(Boolean).join(" ") || "Judoka";
}

export function getAdherentPhoto(adherent) {
  return adherent?.photo_url || DEFAULT_AVATAR;
}

export function getMainCoach(group) {
  const users = getGroupUsers(group);

  if (users.length > 0) return users[0];

  if (group?.coach_name) {
    return {
      nom: group.coach_name,
      prenom: "",
    };
  }

  return null;
}

export function getCoachName(group) {
  const coach = getMainCoach(group);

  return coach ? getUserName(coach) : "Aucun coach";
}

export function getAllCoachNames(group) {
  const users = getGroupUsers(group);

  if (!users.length) {
    return group?.coach_name ? group.coach_name : "Aucun utilisateur";
  }

  return users.map(getUserName).join(", ");
}

export function getStatusLabel(group) {
  return group?.is_active === false ? "Inactif" : "Actif";
}

export function getStatusStyle(group) {
  if (group?.is_active === false) {
    return "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/60";
  }

  return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
}

export function getGroupColorClasses(group) {
  const color = group?.color_groupe || "";

  if (color.includes("green")) {
    return {
      dot: "bg-emerald-500",
      badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
      gradient: "from-emerald-600 to-green-700",
    };
  }

  if (color.includes("red")) {
    return {
      dot: "bg-red-500",
      badge: "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200",
      gradient: "from-red-600 to-rose-700",
    };
  }

  if (color.includes("yellow")) {
    return {
      dot: "bg-amber-500",
      badge: "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200",
      gradient: "from-amber-500 to-orange-600",
    };
  }

  if (color.includes("purple")) {
    return {
      dot: "bg-purple-500",
      badge: "bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-200",
      gradient: "from-purple-600 to-indigo-700",
    };
  }

  if (color.includes("pink")) {
    return {
      dot: "bg-pink-500",
      badge: "bg-pink-100 text-pink-700 dark:bg-pink-500/10 dark:text-pink-200",
      gradient: "from-pink-600 to-rose-700",
    };
  }

  if (color.includes("gray") || color.includes("slate")) {
    return {
      dot: "bg-slate-500",
      badge: "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/60",
      gradient: "from-slate-700 to-slate-900",
    };
  }

  return {
    dot: "bg-blue-500",
    badge: "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
    gradient: "from-sky-600 to-blue-700",
  };
}

export function getUniqueCoaches(groups = []) {
  const names = groups.map(getCoachName).filter((name) => name && name !== "Aucun coach");

  return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, "fr"));
}

export function filterGroups(groups = [], filters = DEFAULT_FILTERS) {
  const search = normalizeText(filters.search);
  const status = String(filters.status || "");
  const coach = normalizeText(filters.coach);
  const members = String(filters.members || "");

  return groups.filter((group) => {
    const groupName = normalizeText(getGroupName(group));
    const coachName = normalizeText(getAllCoachNames(group));
    const adherentsLabel = normalizeText(
      getGroupAdherents(group).map(getAdherentName).join(" "),
    );

    const count = getGroupAdherentsCount(group);
    const isActive = group?.is_active !== false;

    const matchesSearch =
      !search ||
      groupName.includes(search) ||
      coachName.includes(search) ||
      adherentsLabel.includes(search);

    const matchesStatus =
      !status ||
      (status === "active" && isActive) ||
      (status === "inactive" && !isActive);

    const matchesCoach = !coach || normalizeText(getCoachName(group)) === coach;

    const matchesMembers =
      !members ||
      (members === "with" && count > 0) ||
      (members === "without" && count === 0);

    return matchesSearch && matchesStatus && matchesCoach && matchesMembers;
  });
}

export function sortGroups(groups = [], sort = "nameAsc") {
  return [...groups].sort((a, b) => {
    const nameA = normalizeText(getGroupName(a));
    const nameB = normalizeText(getGroupName(b));

    const countA = getGroupAdherentsCount(a);
    const countB = getGroupAdherentsCount(b);

    const orderA = Number(a?.ordre_groupe || 0);
    const orderB = Number(b?.ordre_groupe || 0);

    const idA = Number(a?.id || 0);
    const idB = Number(b?.id || 0);

    if (sort === "nameDesc") return nameB.localeCompare(nameA, "fr");
    if (sort === "membersDesc") return countB - countA;
    if (sort === "membersAsc") return countA - countB;
    if (sort === "orderAsc") return orderA - orderB;
    if (sort === "recent") return idB - idA;
    if (sort === "oldest") return idA - idB;

    return nameA.localeCompare(nameB, "fr");
  });
}

export function paginateItems(items = [], page = 1, perPage = 25) {
  const safePerPage = Number(perPage || 25);
  const totalPages = Math.max(Math.ceil(items.length / safePerPage), 1);
  const safePage = Math.min(Math.max(Number(page || 1), 1), totalPages);
  const start = (safePage - 1) * safePerPage;

  return {
    page: safePage,
    totalPages,
    items: items.slice(start, start + safePerPage),
  };
}

export function buildGroupStats(groups = []) {
  const uniqueAdherents = new Set();
  const uniqueCoaches = new Set();

  let active = 0;
  let inactive = 0;
  let empty = 0;
  let totalLinks = 0;

  groups.forEach((group) => {
    const count = getGroupAdherentsCount(group);

    totalLinks += count;

    if (group?.is_active === false) inactive += 1;
    else active += 1;

    if (count === 0) empty += 1;

    getGroupAdherents(group).forEach((adherent) => {
      if (adherent?.id) uniqueAdherents.add(String(adherent.id));
    });

    getGroupUsers(group).forEach((user) => {
      if (user?.id) uniqueCoaches.add(String(user.id));
    });
  });

  return {
    total: groups.length,
    active,
    inactive,
    empty,
    totalLinks,
    uniqueAdherents: uniqueAdherents.size || totalLinks,
    uniqueCoaches: uniqueCoaches.size,
  };
}